import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Minus,
  Pencil,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useEvidenceArrowNav } from "../../../../../hooks/useEvidenceArrowNav";
import { scrollToVerticalCenter } from "../../../../../lib/scrollToCenter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import { ActivityPopover } from "./ActivityPopover";
import { Badge } from "../../../../../components/ui/badge";
import { type AuditorTrail } from "../../../../../models/audit/attributeTestColumn";
import { type Citation } from "../../../../../models/audit/citation";
import {
  buildTrailDisplay,
  type TrailDisplayItem,
} from "../../lib/trailDisplay";
import { getTrailConclusionDisplay } from "../../lib/trailConclusion";
import { TrailConclusionBody } from "./TrailConclusionBody";

/** The ✓ / ✗ / – marker for a step: ✗ when it's a control exception, an amber
 * dash when the result is inconclusive, otherwise a green check. */
function StepStatusIcon({
  exception,
  inconclusive,
}: {
  exception: boolean;
  inconclusive: boolean;
}) {
  if (exception) {
    return <X className="h-4 w-4 shrink-0 text-red-600" strokeWidth={3} />;
  }
  if (inconclusive) {
    return (
      <Minus className="h-4 w-4 shrink-0 text-amber-500" strokeWidth={3} />
    );
  }
  return (
    <Check className="h-4 w-4 shrink-0 text-emerald-600" strokeWidth={3} />
  );
}

/** The step's status marker. When `onSelect` is supplied it becomes a clickable
 * dropdown to set Pass (✓) / Exception (✗) — persisting the step's `is_exception`
 * flag; otherwise it's a static icon. (Inconclusive is a result-level state, not
 * a per-step choice, so it isn't an option here.) */
function StepStatusControl({
  exception,
  inconclusive,
  onSelect,
}: {
  exception: boolean;
  inconclusive: boolean;
  onSelect?: (isException: boolean) => void;
}) {
  if (!onSelect) {
    return (
      <span className="self-center">
        <StepStatusIcon exception={exception} inconclusive={inconclusive} />
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Set step status"
          className="flex shrink-0 items-center self-center rounded p-0.5 transition-colors hover:bg-stone-300/60"
        >
          <StepStatusIcon exception={exception} inconclusive={inconclusive} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem]">
        <DropdownMenuItem
          onClick={() => onSelect(false)}
          className={`gap-2 ${!exception ? "font-medium" : ""}`}
        >
          <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
          Pass
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onSelect(true)}
          className={`gap-2 ${exception ? "font-medium" : ""}`}
        >
          <X className="h-4 w-4 text-red-600" strokeWidth={3} />
          Exception
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Modal editor for a step's text or a citation's summary — mirrors the prod
 * "Edit Evidence Summary" dialog. Cmd/Ctrl+Enter saves; Save commits only when
 * the text changed and is non-empty. */
function EditSummaryDialog({
  open,
  title,
  description,
  value,
  onSave,
  onClose,
  onDelete,
  fieldLabel = "Summary",
}: {
  open: boolean;
  title: string;
  description: string;
  value: string;
  onSave: (next: string) => void;
  onClose: () => void;
  /** When provided, a red trash button in the footer deletes the underlying
   * item (and closes the dialog). */
  onDelete?: () => void;
  /** Caption above the textarea. Defaults to "Summary"; the L1 editor passes
   * "Conclusion" so the label matches what's being edited. */
  fieldLabel?: string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== value) onSave(next);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-stone-700">
            {fieldLabel} <span className="text-red-500">*</span>
          </label>
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                commit();
              }
            }}
            rows={5}
            className="w-full resize-y rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
          />
        </div>
        <DialogFooter>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              title="Delete tickmark"
              className="rounded-md p-2 text-red-600 transition-colors hover:bg-red-50 sm:mr-auto"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-stone-200 px-3 py-1.5 text-sm text-stone-700 transition-colors hover:bg-stone-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={commit}
            className="rounded-md bg-stone-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-stone-700"
          >
            Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AuditorTrailPanelProps {
  auditorTrail: AuditorTrail | null | undefined;
  /** Attribute-test column code (e.g. "A1"/"A2" for columns sharing attribute
   * "A", or "B" for a solo column) used to prefix citation-row codes: "A1.1",
   * "B.1", … numbered continuously across the column's steps. Falls back to a
   * bare number when absent. */
  attributeCode?: string;
  /** When provided, citation rows drive the document viewer (the inline
   * fold-out wires this to the shared `updateDocumentForCitation`). Receives the
   * citation whose document should be shown — the screenshot when a row has one,
   * otherwise the source. */
  onCitationClick?: (citation: Citation) => void;
  /** When provided, the "In Workpaper" column becomes a clickable star that
   * toggles the citation's `is_key`. Called once per underlying citation (both
   * the source and its screenshot, when paired) so the export stays consistent. */
  onToggleInWorkpaper?: (citation: Citation) => void;
  /** The citation currently highlighted in the viewer — its row is marked
   * active and its step auto-expands. */
  activeCitationId?: string;
  /** Whether a document id is a screenshot child (e.g. `excel_screenshot`). Used
   * to collapse a live-source citation and its screenshot sibling into one row.
   * Absent → no collapsing (every citation is its own row). */
  isScreenshotDoc?: (documentId: string | null | undefined) => boolean;
  /** The result is inconclusive (no definitive pass/fail). Steps then show a
   * neutral marker instead of a green check — a green ✓ on an inconclusive
   * verdict would falsely read as "all passed". */
  inconclusive?: boolean;
  /** When provided, the step status marker becomes a clickable Pass/Exception
   * dropdown that persists the step's `is_exception` flag. */
  onSetStepStatus?: (stepId: string, isException: boolean) => void;
  /** When provided, an edit pencil on the L1 verdict (the "Conclusion") lets the
   * auditor rewrite the trail's `l1` test conclusion. */
  onSetL1?: (l1: string) => void;
  /** When provided, an edit pencil on each L2 step lets the auditor rewrite the
   * step's `text`. */
  onSetStepText?: (stepId: string, text: string) => void;
  /** When provided, an edit pencil on each citation row lets the auditor rewrite
   * its `short_summary`. */
  onUpdateCitationSummary?: (citationId: string, summary: string) => void;
  /** When provided, the edit-summary dialog gets a delete button that removes the
   * citation (the tickmark). Called per underlying citation (source + screenshot
   * when paired). */
  onDeleteCitation?: (citation: Citation) => void;
  /** The result whose column-action-log feed (generations, status changes,
   * edits) backs the "Activity" popover. The popover fetches lazily on open. */
  attributeTestResultId?: string;
  /** Human-readable control-exception labels (the result's fail modes, e.g.
   * "Improper Authorization") shown as pills beneath the L1 conclusion. These are
   * result-level, not per-step — empty/absent when the result didn't fail or has
   * no exceptions. */
  controlExceptions?: string[];
  /** The result's `potential_mitigating_factors` text, shown as a labelled block
   * beneath the control-exception pills. Result-level (conclusive results only)
   * and typically only populated on failing results — absent/empty hides the
   * block. */
  potentialMitigatingFactors?: string | null;
}

/**
 * The persisted auditor trail (Evidence Walk V3) as an accordion:
 * the L1 verdict up top, then each L2 step as a collapsible row
 * (status check/✗ + summary) whose expanded body lists the step's evidence in a
 * `# | Summary | In Workpaper` table, numbered continuously across the
 * attribute's steps ("A1.1", "A1.2", …). A live document and its rendered
 * screenshot collapse to one row (they share an `encodedCitationString`).
 * Exception steps (`isException`) are styled red. Rows are clickable when
 * `onCitationClick` is supplied.
 */
export function AuditorTrailPanel({
  auditorTrail,
  attributeCode,
  onCitationClick,
  onToggleInWorkpaper,
  activeCitationId,
  isScreenshotDoc,
  inconclusive,
  onSetStepStatus,
  onSetL1,
  onSetStepText,
  onUpdateCitationSummary,
  onDeleteCitation,
  attributeTestResultId,
  controlExceptions,
  potentialMitigatingFactors,
}: AuditorTrailPanelProps) {
  const [openSteps, setOpenSteps] = useState<Set<number>>(() => new Set([0]));
  // Whether the L1 conclusion is currently in the edit dialog.
  const [editingL1, setEditingL1] = useState(false);
  // Which step text / citation summary is currently in inline-edit mode.
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editingCitation, setEditingCitation] = useState<string | null>(null);
  // The scrollable step list — used to centre a citation row on expand.
  const scrollRef = useRef<HTMLDivElement>(null);
  // The horizontal tickmark-chip strip — used to keep the active chip in view.
  const tickmarkStripRef = useRef<HTMLDivElement>(null);

  // Scroll the step list so the given citation row is centred (called when a step
  // is expanded; the timeout lets the now-visible table render first).
  const centerCitation = (citationId: string) => {
    window.setTimeout(() => {
      const container = scrollRef.current;
      if (!container) return;
      const row = container.querySelector(`[data-citation-id="${citationId}"]`);
      if (row instanceof HTMLElement) scrollToVerticalCenter(container, row);
    }, 70);
  };

  // Reveal the step that owns the active citation (e.g. after a doc-viewer
  // click or a re-target) so the highlighted evidence is always visible.
  useEffect(() => {
    if (!activeCitationId || !auditorTrail) return;
    const idx = auditorTrail.steps.findIndex((step) =>
      step.citations.some((c) => c.id === activeCitationId),
    );
    if (idx >= 0) {
      setOpenSteps((prev) => (prev.has(idx) ? prev : new Set([idx])));
    }
  }, [activeCitationId, auditorTrail]);

  const displaySteps = useMemo(
    () =>
      auditorTrail
        ? buildTrailDisplay(
            auditorTrail,
            attributeCode,
            isScreenshotDoc ?? (() => false),
          )
        : [],
    [auditorTrail, attributeCode, isScreenshotDoc],
  );

  // Flat, ordered list of every evidence item (one per collapsed pair) across all
  // steps — drives the bottom tickmark-nav strip.
  const allItems = useMemo(
    () => displaySteps.flatMap((s) => s.items),
    [displaySteps],
  );

  // Centre the active citation's row whenever it changes from outside (e.g. a
  // tickmark click in the doc viewer), once its step is open. The id may be a
  // screenshot's, so centre on the owning row's source id.
  useEffect(() => {
    if (!activeCitationId) return;
    const item = allItems.find(
      (it) =>
        it.source.id === activeCitationId ||
        it.screenshot?.id === activeCitationId,
    );
    if (item) centerCitation(item.source.id);
  }, [activeCitationId, allItems]);

  // Keyboard nav over the distinct evidence items (the bottom tickmark strip):
  // one press = one move, even when a pair's citations are non-contiguous (e.g.
  // after an add/remove/import reorders citation_order).
  useEvidenceArrowNav({
    items: allItems,
    activeCitationId,
    onSelect: (citation) => onCitationClick?.(citation),
  });

  // Keep the active chip in view in the bottom tickmark strip: when the active
  // citation moves to a chip that's scrolled out of bounds, nudge the strip
  // horizontally to reveal it (with a little padding).
  useEffect(() => {
    if (!activeCitationId) return;
    const item = allItems.find(
      (it) =>
        it.source.id === activeCitationId ||
        it.screenshot?.id === activeCitationId,
    );
    const container = tickmarkStripRef.current;
    if (!item || !container) return;
    const chip = container.querySelector(`[data-chip-id="${item.source.id}"]`);
    if (!(chip instanceof HTMLElement)) return;
    const containerRect = container.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    if (chipRect.left < containerRect.left) {
      container.scrollBy({
        left: chipRect.left - containerRect.left - 8,
        behavior: "smooth",
      });
    } else if (chipRect.right > containerRect.right) {
      container.scrollBy({
        left: chipRect.right - containerRect.right + 8,
        behavior: "smooth",
      });
    }
  }, [activeCitationId, allItems]);

  if (!auditorTrail) {
    return null;
  }

  // Single-open accordion: opening an L2 step collapses the others; clicking the
  // open step closes it.
  const toggle = (index: number) =>
    setOpenSteps((prev) =>
      prev.has(index) ? new Set<number>() : new Set([index]),
    );

  // The citation whose document the viewer should show for a row: the screenshot
  // when present (so the rendered image is the default view), otherwise the
  // source.
  const viewCitation = (item: TrailDisplayItem): Citation =>
    item.screenshot ?? item.source;

  const handleStar = (item: TrailDisplayItem) => {
    onToggleInWorkpaper?.(item.source);
    if (item.screenshot) onToggleInWorkpaper?.(item.screenshot);
  };

  // Bottom tickmark-nav strip: jump to any citation by its code, or step through
  // with the prev/next arrows. The active item is the one currently shown in the
  // viewer.
  const isItemActive = (item: TrailDisplayItem) =>
    item.source.id === activeCitationId ||
    item.screenshot?.id === activeCitationId;
  const activeIndex = allItems.findIndex(isItemActive);
  const goToIndex = (i: number) => {
    const item = allItems[i];
    if (item) onCitationClick?.(viewCitation(item));
  };

  // The step / citation (if any) currently open in the edit dialog.
  const stepBeingEdited =
    editingStep !== null ? (auditorTrail.steps[editingStep] ?? null) : null;
  const citationBeingEdited = editingCitation
    ? (allItems.find((it) => it.source.id === editingCitation) ?? null)
    : null;

  // Inconclusive results show a fixed, non-editable Conclusion (the model's L1
  // can contradict the verdict); conclusive ones show the editable L1.
  const conclusion = getTrailConclusionDisplay({
    inconclusive: inconclusive === true,
    l1: auditorTrail.l1,
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="group shrink-0 px-3 pb-3 pt-3">
        <div className="mb-1 flex items-center gap-1.5">
          <span className="text-sm font-semibold text-stone-700">
            Conclusion
          </span>
          {onSetL1 && conclusion.editable && (
            <button
              type="button"
              onClick={() => setEditingL1(true)}
              title="Edit conclusion"
              className="flex shrink-0 items-center rounded p-0.5 text-stone-400 opacity-0 transition-colors hover:bg-stone-200 hover:text-stone-600 group-hover:opacity-100"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <TrailConclusionBody
          conclusion={conclusion}
          onAddConclusion={onSetL1 ? () => setEditingL1(true) : undefined}
        />
        {controlExceptions && controlExceptions.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-font-secondary">
              Control Exception{controlExceptions.length > 1 ? "s" : ""}:
            </span>
            {controlExceptions.map((label) => (
              <Badge key={label} variant="outline" className="bg-stone-50">
                {label}
              </Badge>
            ))}
          </div>
        )}
        {potentialMitigatingFactors && (
          <div className="mt-2 border-l-2 border-stone-300 pl-2">
            <div className="text-xs font-medium text-font-secondary">
              Potential Mitigating Factors:
            </div>
            {/* Free-form AI text with no length bound — cap the block so a long
                value can't inflate the shrink-0 header and crush the step list. */}
            <div className="mt-0.5 max-h-40 overflow-y-auto whitespace-pre-wrap text-sm">
              {potentialMitigatingFactors}
            </div>
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto border-t border-stone-200"
      >
        {auditorTrail.steps.map((step, index) => {
          const open = openSteps.has(index);
          const exception = step.isException === true;
          const items = displaySteps[index]?.items ?? [];
          const isFirst = index === 0;
          const prevOpen = index > 0 && openSteps.has(index - 1);
          const nextOpen = openSteps.has(index + 1);
          // An open step is framed with a divider below — and above too, unless
          // it's the top step (which already sits under the panel's top border).
          const stepBorder = open
            ? isFirst
              ? "border-b border-stone-100"
              : "border-y border-stone-100"
            : "";
          // The top L2, and any closed step sitting against an open step's divider
          // line, get the full inter-step gap (pt-2 / pb-2 ≈ the pb-1 + pt-1
          // between two closed steps) so the gap to a divider matches the gap
          // between closed L2s. Open steps keep their larger internal room.
          const padTop = open
            ? isFirst
              ? "pt-2"
              : "pt-4"
            : isFirst || prevOpen
              ? "pt-2"
              : "pt-1";
          const padBottom = open ? "pb-4" : nextOpen ? "pb-2" : "pb-1";
          return (
            <div key={step.id} className={stepBorder}>
              <div className={`px-3 ${padTop} ${padBottom}`}>
                <div className="group flex w-full items-start gap-2 rounded-md bg-stone-100 px-2 py-1.5 transition-colors hover:bg-stone-200">
                  <button
                    type="button"
                    onClick={() => {
                      const willOpen = !openSteps.has(index);
                      toggle(index);
                      // Opening a step focuses its first evidence item (same as
                      // clicking that row) so the doc viewer jumps to it, and
                      // centres that row in the step list.
                      if (willOpen && items.length > 0) {
                        onCitationClick?.(viewCitation(items[0]));
                        centerCitation(items[0].source.id);
                      }
                    }}
                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                  >
                    {open ? (
                      <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-stone-400" />
                    ) : (
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-stone-400" />
                    )}
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-white text-xs font-semibold text-stone-900 shadow-sm">
                      {index + 1}
                    </span>
                    <span
                      className={`min-w-0 flex-1 text-sm ${
                        open ? "whitespace-pre-wrap" : "line-clamp-2"
                      }`}
                    >
                      {step.text}
                    </span>
                  </button>
                  {onSetStepText && (
                    <button
                      type="button"
                      onClick={() => setEditingStep(index)}
                      title="Edit step"
                      className="flex shrink-0 items-center self-center rounded p-0.5 text-stone-400 opacity-0 transition-colors hover:bg-stone-300/60 hover:text-stone-600 group-hover:opacity-100"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <StepStatusControl
                    exception={exception}
                    inconclusive={!!inconclusive}
                    onSelect={
                      onSetStepStatus
                        ? (isException) => onSetStepStatus(step.id, isException)
                        : undefined
                    }
                  />
                </div>
              </div>

              {open && items.length > 0 && (
                <div className="px-3 pb-4 pl-9">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-stone-50 text-left text-xs text-font-secondary">
                        <th className="w-14 border border-stone-200 px-2 py-1.5 font-medium">
                          #
                        </th>
                        <th className="border border-stone-200 px-2 py-1.5 font-medium">
                          Summary
                        </th>
                        <th className="w-24 border border-stone-200 px-2 py-1.5 text-center font-medium">
                          In Workpaper
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const active =
                          item.source.id === activeCitationId ||
                          item.screenshot?.id === activeCitationId;
                        const summary =
                          item.source.shortSummary || `Citation ${item.code}`;
                        const isKey = item.source.isKey;
                        return (
                          <tr
                            key={item.source.id}
                            data-citation-id={item.source.id}
                            // Fade the unselected citations so the selected one
                            // (its step's other rows) reads as the focus.
                            className={`group transition-opacity ${
                              active
                                ? "bg-blue-50"
                                : activeCitationId
                                  ? "opacity-40"
                                  : ""
                            }`}
                          >
                            <td
                              onClick={() =>
                                onCitationClick?.(viewCitation(item))
                              }
                              className={`border border-stone-200 px-2 py-1.5 text-center align-middle font-mono text-xs font-semibold ${
                                onCitationClick ? "cursor-pointer" : ""
                              }`}
                            >
                              {item.code}
                            </td>
                            <td
                              onClick={() =>
                                onCitationClick?.(viewCitation(item))
                              }
                              className={`border border-stone-200 px-2 py-1.5 align-top ${
                                onCitationClick ? "cursor-pointer" : ""
                              }`}
                            >
                              <div className="flex items-start gap-1.5">
                                <span className="min-w-0 flex-1">
                                  {summary}
                                </span>
                                {onUpdateCitationSummary && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingCitation(item.source.id);
                                    }}
                                    title="Edit summary"
                                    className="shrink-0 rounded p-0.5 text-stone-300 opacity-0 transition-colors hover:bg-stone-100 hover:text-stone-600 group-hover:opacity-100"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="border border-stone-200 px-2 py-1.5 text-center align-middle">
                              {onToggleInWorkpaper ? (
                                <button
                                  type="button"
                                  onClick={() => handleStar(item)}
                                  title={
                                    isKey
                                      ? "Remove from workpaper"
                                      : "Include in workpaper"
                                  }
                                  className="inline-flex items-center justify-center rounded p-1 hover:bg-stone-100"
                                >
                                  <Star
                                    className={`h-4 w-4 ${
                                      isKey
                                        ? "fill-amber-400 text-amber-400"
                                        : "text-stone-300"
                                    }`}
                                  />
                                </button>
                              ) : (
                                <Star
                                  className={`mx-auto h-4 w-4 ${
                                    isKey
                                      ? "fill-amber-400 text-amber-400"
                                      : "text-stone-300"
                                  }`}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer is always present so the Activity feed stays reachable even when
          the result has no tickmarks yet (generations/status changes are logged
          independently of citations). The tickmark strip is gated; Activity is
          not. */}
      <div className="shrink-0 border-t border-stone-200 px-3 py-2">
        {onCitationClick && allItems.length > 0 && (
          <>
            <div className="mb-1 font-mono text-xs text-font-secondary">
              Tickmarks
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  goToIndex(activeIndex <= 0 ? 0 : activeIndex - 1)
                }
                title="Previous tickmark"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-stone-200 text-stone-600 transition-colors hover:bg-stone-100"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div
                ref={tickmarkStripRef}
                className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto"
              >
                {allItems.map((item, i) => {
                  const active = isItemActive(item);
                  return (
                    <button
                      key={item.source.id}
                      data-chip-id={item.source.id}
                      type="button"
                      onClick={() => goToIndex(i)}
                      title={item.source.shortSummary ?? item.code}
                      className={`flex shrink-0 items-center gap-1 rounded border px-2 py-1 font-mono text-xs transition-colors ${
                        active
                          ? "border-stone-900 bg-stone-900 text-white"
                          : "border-stone-200 text-stone-600 hover:bg-stone-100"
                      }`}
                    >
                      {item.source.isKey && (
                        <Star
                          className={`h-3 w-3 ${
                            active
                              ? "fill-amber-300 text-amber-300"
                              : "fill-amber-400 text-amber-400"
                          }`}
                        />
                      )}
                      {item.code}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() =>
                  goToIndex(
                    activeIndex < 0
                      ? 0
                      : Math.min(allItems.length - 1, activeIndex + 1),
                  )
                }
                title="Next tickmark"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-stone-200 text-stone-600 transition-colors hover:bg-stone-100"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
        <div
          className={
            onCitationClick && allItems.length > 0 ? "mt-2 flex" : "flex"
          }
        >
          <ActivityPopover attributeTestResultId={attributeTestResultId} />
        </div>
      </div>

      {editingL1 && (
        <EditSummaryDialog
          open
          title="Edit Test Conclusion"
          description="Update the L1 verdict for this attribute test."
          fieldLabel="Conclusion"
          value={auditorTrail.l1}
          onSave={(t) => onSetL1?.(t)}
          onClose={() => setEditingL1(false)}
        />
      )}
      {stepBeingEdited && (
        <EditSummaryDialog
          open
          title="Edit Step Summary"
          description="Update the summary for this step."
          value={stepBeingEdited.text}
          onSave={(t) => onSetStepText?.(stepBeingEdited.id, t)}
          onClose={() => setEditingStep(null)}
        />
      )}
      {citationBeingEdited && (
        <EditSummaryDialog
          open
          title="Edit Tickmark Summary"
          description="Update the summary for this tickmark."
          value={citationBeingEdited.source.shortSummary ?? ""}
          onSave={(t) =>
            onUpdateCitationSummary?.(citationBeingEdited.source.id, t)
          }
          onClose={() => setEditingCitation(null)}
          onDelete={
            onDeleteCitation
              ? () => {
                  onDeleteCitation(citationBeingEdited.source);
                  if (citationBeingEdited.screenshot)
                    onDeleteCitation(citationBeingEdited.screenshot);
                  setEditingCitation(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
